/** @jsx jsx */
/** @jsxRuntime classic */

import React from "react";
import { jsx } from "@emotion/core";
import classNames from "classnames";

type Scores = {
  ratingValue: number;
  fractionOfRatings: number;
};

type SummaryCardProps = {
  site: string;
  reviews: number;
  breakdown: Scores[];
  href?: string | null;
};

const SummaryCard: React.FC<SummaryCardProps> = ({ site, reviews, breakdown, href }) => {
  function calculateAverage() {
    if (reviews === 0 || !breakdown) return "N/A";
    const num = reviews / 100;
    let score = 1;
    let sum = 0;

    breakdown.forEach((e) => {
      const percentage = e.fractionOfRatings * 100;
      const ratings = num * percentage;
      const total = ratings * score;
      score = score + 1;
      sum = sum + total;
    });

    return (sum / reviews).toFixed(2);
  }

  const avg = parseFloat(calculateAverage());

  const getHeaderColour = () => {
    switch (true) {
      case avg > 0 && avg <= 1:
        return "VeryNegativeHeader";
      case avg > 1 && avg <= 2:
        return "NegativeHeader";
      case avg > 2 && avg <= 3:
        return "NeutralHeader";
      case avg > 3 && avg <= 4:
        return "PositiveHeader";
      case avg > 4 && avg <= 5:
        return "VeryPositiveHeader";
      default:
        return "NotApplicable";
    }
  };

  const headerColour = getHeaderColour();
  const className = classNames("SummaryCardHeader", headerColour);

  const content = (
    <React.Fragment>
      <div className={className}></div>
      <div className="SummaryCardBody">
        <div className="SummaryCardSiteScore">
          <h6>{site}</h6>
          <p className="Figure">{calculateAverage()}</p>
        </div>
        <div className="SummaryCardReviewTotal">
          <h6>Reviews</h6>
          <p className="Figure">{reviews.toLocaleString("en-GB")}</p>
        </div>
      </div>
      <div className="SummaryCardFooter">
        <h6 className="BreakdownCaption GreyTxt">Score breakdown</h6>
        <div className="BreakdownFlex">
          <div>
            <p className="GreyTxt">1</p>
            <p className="VeryNegative">{(breakdown[0].fractionOfRatings * 100).toFixed(1)}%</p>
          </div>
          <div>
            <p className="GreyTxt">2</p>
            <p className="Negative">{(breakdown[1].fractionOfRatings * 100).toFixed(1)}%</p>
          </div>
          <div>
            <p className="GreyTxt">3</p>
            <p className="Neutral">{(breakdown[2].fractionOfRatings * 100).toFixed(1)}%</p>
          </div>
          <div>
            <p className="GreyTxt">4</p>
            <p className="Positive">{(breakdown[3].fractionOfRatings * 100).toFixed(1)}%</p>
          </div>
          <div>
            <p className="GreyTxt">5</p>
            <p className="VeryPositive">{(breakdown[4].fractionOfRatings * 100).toFixed(1)}%</p>
          </div>
        </div>
      </div>
    </React.Fragment>
  );

  if (href)
    return (
      <a href={href} className="SummaryCard">
        {content}
      </a>
    );

  return <div className="SummaryCard">{content}</div>;
};

export default SummaryCard;
